import React, { createContext, SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionStatus,
  PublicClientApplication,
} from "@azure/msal-browser";
import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEmpty } from "lodash";
import { jwtDecode } from "jwt-decode";
import { SnackbarProvider } from "notistack";

import {
  checkIsTextPresent,
  checkRoleData,
  checkSSOUser,
  encryptId,
  getSessionStorage,
  getValue,
  hasAccess,
  setSessionStorage,
} from "../../utils";
import { AppConfigs } from "../../models/AppConfigs";
import { loginRequest } from "../..";
import { Header, LeftNavbar } from "..";
import toastType, { SnackbarUtilsConfigurator } from "../../utils/SnackbarProvider";
import { MyAccountModel } from "../../models/MyAccount";
import {
  JwtPayload,
  RefreshTokenPayload,
  RegisterUser,
  UserByClaims,
  UserMfaInfo,
} from "../../models/User";
import {
  Bcbs_Tn_SSO_ActionDispatchTypes,
  getValidMessageType,
  MFAStatus,
  UserMfaInfoActionDispatchTypes,
} from "../../constants/User";
import {
  HeaderStickyContainer,
  LeftNavStickyContainer,
  SnackbarAnchorOrigin,
  UnauthenticatedContainer,
} from "../styles/styles";
import { b2cScope, configs, configUrl, redirectUri } from "../../constants/Config";
import { ApplicationRoute } from "../../constants/ApplicationUrl";
import UserRegistration from "../containers/UserRegistration";
import { userByClaimsDefaultData } from "../../reducers/GetUserByClaims";
import CentralLoader from "./CentralLoader";
import ForgotPassword from "../containers/ForgotPassword";
import ResetPassword from "../containers/ResetPassword";
import { Constants, Numbers } from "../../constants/Referral";
import { MFAContent } from "../common";
import { MessageType, ResponseMessages } from "../../constants/ResponseMessage";
import { resetStates } from "../../actions/Common";
import SessionTimeout from "../common/SessionTimeoutModal";
import { FeatureAccess, MasterRole } from "../../models/Master";
declare global {
  interface Window {
    fcWidget?: any;
    fcSettings?: any;
  }
}

export interface PropsFromState {
  config: AppConfigs;
  loggedInUser: MyAccountModel;
  instance: PublicClientApplication;
  userSendMfaInfoState: any; //any required as error is not an specific type
  userVerifyMfaInfoState: UserMfaInfo;
  loggedInUserFlow: number;
  userByClaimsState: any;
  registerUserState: RegisterUser;
  userSendMfaInfoErrorState: any;
  masterRoleState: MasterRole[];
  masterFeatureAccessState: FeatureAccess[] | null;
  samlResponseState: any;
  userLogoutState: any;
  refreshTokenResponseState: any;
}
export interface PropsFromDispatch {
  getLoggedInUserByEmail: (userEmail: string) => void;
  getConfig: () => void;
  sendMfaCode: (payload: UserMfaInfo) => void;
  resendMfaCode: (payload: UserMfaInfo) => void;
  verifyMfaCode: (payload: UserMfaInfo) => void;
  resetStates: (actionType: string) => void;
  getUserByClaims: (headers: any) => void;
  resetUserByClaims: () => void;
  getMasterRole: () => void;
  getFeatureAccessData: () => void;
  getSAML: (payload: any) => void;
  logoutPortalApp: () => void;
  getRefreshToken: (payload: RefreshTokenPayload) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;
export interface UserContextType {
  roleId: string;
}
export const UserContext = createContext<UserContextType>({
  roleId: "",
});

const AppContent: React.FC<AllProps> = ({
  getConfig,
  getLoggedInUserByEmail,
  loggedInUser,
  sendMfaCode,
  resendMfaCode,
  userSendMfaInfoState,
  verifyMfaCode,
  userVerifyMfaInfoState,
  loggedInUserFlow,
  getUserByClaims,
  userByClaimsState,
  registerUserState,
  resetUserByClaims,
  userSendMfaInfoErrorState,
  getMasterRole,
  masterRoleState,
  getFeatureAccessData,
  getSAML,
  samlResponseState,
  logoutPortalApp,
  userLogoutState,
  masterFeatureAccessState,
  config,
  refreshTokenResponseState,
  getRefreshToken,
}) => {
  const [mfaVerified, setMfaVerified] = useState<boolean>(false);
  const [openMfaModal, setOpenMfaModal] = useState<boolean>(false);
  const [userByClaims, setUserByClaims] = useState<UserByClaims>(
    userByClaimsDefaultData,
  );

  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const url = location.pathname;
  const isTextPresent = checkIsTextPresent(url);
  const urlParams = new URLSearchParams(location.search);
  const ssoError = urlParams.get("error");
  const isForgotpassword = encodeURIComponent(
    urlParams.get(Constants.FORGOT_PASSWORD) ?? "",
  );
  const { inProgress, instance } = useMsal();

  const refreshTimerRef = useRef<number | null>(null);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const scheduleRefresh = (expiresInSeconds: number, expiredAccessToken: string) => {
    clearRefreshTimer();
    const refreshBefore = Math.min(300, Math.floor(expiresInSeconds / 2));
    const ms = (expiresInSeconds - refreshBefore) * 1000;
    if (ms <= 0) {
      getRefreshToken({ expiredAccessToken });
      return;
    }
    refreshTimerRef.current = window.setTimeout(() => {
      getRefreshToken({ expiredAccessToken });
    }, ms) as unknown as number;
  };

  useEffect(() => {
     return () => {
       clearRefreshTimer();
     };
   }, []);
  
  const navigate = useNavigate();

  const code = encodeURIComponent(urlParams.get(Constants.CODE) ?? "");
  const [isSAMLTokenAvailable, setIsSAMLTokenAvailable] =
    useState<boolean>(false);
  const [samlReferralRoute, setSamlReferralRoute] = useState<string | null>(null);

  useEffect(() => {
    if (isForgotpassword === Constants.TRUE) {
      const forgotpasswordUrl = `${ApplicationRoute.FORGOT_PASSWORD}?code=${Numbers.ONE}`;
      navigate(forgotpasswordUrl);
    }
  }, [isForgotpassword]);

useEffect(() => {
    if (!isEmpty(ssoError) && ssoError !== "") {
      sessionStorage.clear();
      setIsSAMLTokenAvailable(false);
      setSamlReferralRoute(null);
      navigate("/", { replace: true });
      instance.loginRedirect({
        ...loginRequest,
        prompt: "login",
        redirectUri: redirectUri,
      });
    }
  }, [ssoError]);

  useEffect(() => {
    const clearSamlOnExit = (event: PageTransitionEvent) => {
      if (event.persisted) {
        return;
      }
      const samlToken = getSessionStorage("samlToken");
      if (!isEmpty(samlToken) && samlToken.length > Numbers.ZERO) {
        window.sessionStorage.removeItem("samlToken");
        window.sessionStorage.removeItem("samlUserEmail");
        setIsSAMLTokenAvailable(false);
        setSamlReferralRoute(null);
        resetStates(Bcbs_Tn_SSO_ActionDispatchTypes.BCBS_TN_SSO_RESET);
      }
    };

    window.addEventListener("pagehide", clearSamlOnExit);

    return () => {
      window.removeEventListener("pagehide", clearSamlOnExit);
    };
  }, [resetStates]);

  useEffect(() => {
    if (
      getValue(userByClaimsState, Constants.STATUS) === 400 ||
      getValue(userByClaimsState, Constants.STATUS) === 404
    ) {
      instance.loginRedirect(loginRequest);
    } else if (
      getValue(userByClaimsState, Constants.STATUS) === 200 ||
      getValue(userByClaimsState, Constants.STATUS) === 201
    ) {
      setUserByClaims(userByClaimsState.data);
    } else {
      setUserByClaims(userByClaimsDefaultData);
    }
  }, [userByClaimsState]);

  useEffect(() => {
    if (userByClaims && !isEmpty(code)) {
      if (isTextPresent === Numbers.TWO) {
        const userRegistrationUrl = `${ApplicationRoute.USER_REGISTRATION}?code=${code}`;
        navigate(userRegistrationUrl);
      } else if (isTextPresent === Numbers.ONE) {
        const resetPassUrl = `${ApplicationRoute.RESET_PASSWORD}?code=${code}`;
        navigate(resetPassUrl);
      }
    }
  }, [userByClaims]);

  useEffect(() => {
    if (
      loggedInUser &&
      getValue(loggedInUser, "providerPortalUser.email").length > 0
    ) {
      window.fcSettings = {
        onInit: function () {
          window.fcWidget.setExternalId(
            getValue(loggedInUser, "providerPortalUser.userId"),
          );
          window.fcWidget.user.setProperties({
            firstName: getValue(loggedInUser, "providerPortalUser.firstName"),
            lastName: getValue(loggedInUser, "providerPortalUser.lastName"),
            email: getValue(loggedInUser, "providerPortalUser.email"),
            phone: getValue(loggedInUser, "providerPortalUser.phoneNumber"),
            phoneCountry: getValue(
              loggedInUser,
              "providerPortalUser.countryCode",
            ),
            //plan: "Pro",  //IF WE HAVE PLAN THEN WE CAN SET
            status: "Active",
          });
        },
      };
      const script = document.createElement("script");
      script.src = configs.REACT_APP_PORTAL_FRESHCHAT_URL;
      script.setAttribute("chat", "true");
      script.setAttribute("widgetId", configs.REACT_APP_PORTAL_FRESHCHAT_WIDGET);
      script.async = true;
      document.body.appendChild(script);
    }
  }, [loggedInUser]);

  useEffect(() => {
    return () => {
      resetUserByClaims();
    };
  }, []);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const bcbsTnUser = () => {
    const params = new URLSearchParams(location.search);
    if (!isEmpty(getSessionStorage("samlToken"))) {
      setIsSAMLTokenAvailable(true);
      getLoggedInUserByEmail(getSessionStorage("samlUserEmail"));
    }
    return params.has("samlCode");
  };

  useEffect(() => {
    if (!isEmpty(ssoError)) {
      return;
    }
    if (
      inProgress === InteractionStatus.None &&
      !isAuthenticated &&
      isEmpty(code) &&
      !bcbsTnUser()
    ) {
      instance.loginRedirect(loginRequest);
    } else if (bcbsTnUser()) {
      extractData();
    }
  }, [isAuthenticated, instance, inProgress, code, ssoError]);

  const extractData = () => {
    const params = new URLSearchParams(location.search);
    const dataSaml = params.get("samlCode");
    setIsChecked(true);
    if (
      dataSaml &&
      !isEmpty(dataSaml) &&
      !getValue(samlResponseState, "accessToken") &&
      isChecked === false
    ) {
      setTimeout(() => {
        getSAML({
          code: dataSaml,
        });
      }, 1000);
    }
  };

  useEffect(() => {
    if (!isEmpty(registerUserState.email)) {
      instance.loginRedirect(loginRequest);
    }
  }, [registerUserState]);

  useEffect(() => {
    if (configUrl && !isEmpty(configUrl)) {
      getConfig();
    }
  }, [configUrl]);

  useEffect(() => {
    if (!isEmpty(code)) {
      const headers = {
        "x-portl-claim-key": code,
      };
      getUserByClaims(headers);
    }
  }, [code]);

  useEffect(() => {
    if (!isEmpty(userVerifyMfaInfoState.status)) {
      if (userVerifyMfaInfoState.status === MFAStatus.SUCCESS) {
        setMfaVerified(true);
        window.sessionStorage.setItem("mfaVerified", "true");
        setOpenMfaModal(false);
      } else {
        setMfaVerified(false);
        window.sessionStorage.setItem("mfaVerified", "false");
      }
      userVerifyMfaInfoState.status !== undefined &&
        getValidMessageType(userVerifyMfaInfoState.status);
    }
  }, [userVerifyMfaInfoState]);

  useEffect(() => {
    const isSSOUser = checkSSOUser();
    if (loggedInUserFlow === Numbers.ZERO && isSSOUser === false) {
      callMfaCode(loggedInUser, Numbers.ONE);
    }
  }, [loggedInUser]);
  //-----------------------------------------------------
  useEffect(() => {
    if (checkRoleData(masterRoleState)) {
      getMasterRole();
    }
  }, [loggedInUser, masterRoleState])

  useEffect(() => {
    if (checkRoleData(masterRoleState) === false) {
      getFeatureAccessData();
    }
  }, [masterRoleState])
  //-----------------------------------------------------

  useEffect(() => {
    if (userSendMfaInfoState.isVerified) {
      setMfaVerified(true);
      window.sessionStorage.setItem("mfaVerified", "true");
      setOpenMfaModal(true);
    } else if (
      !isEmpty(userSendMfaInfoState.status) &&
      !userSendMfaInfoState.isVerified
    ) {
      setMfaVerified(false);
      setOpenMfaModal(true);
      getValidMessageType(userSendMfaInfoState.status);
    }
  }, [userSendMfaInfoState]);

  useEffect(() => {
    if (
      !isEmpty(userSendMfaInfoErrorState) &&
      userSendMfaInfoErrorState.data ===
      ResponseMessages(MessageType.DEACTIVE_USER)
    ) {
      logoutPortalApp();
      resetStates(UserMfaInfoActionDispatchTypes.USER_SEND_MFA_CODE_RESET);
    }
  }, [userSendMfaInfoErrorState]);

   useEffect(() => {
      const handleLogoutRedirect = async () => {
        if (userLogoutState && getValue(userLogoutState, "response.status") === 200) {
          try {
            await instance.logoutRedirect({
              postLogoutRedirectUri: redirectUri,
            });
          } catch (error) {
            console.error(error);
          }
          sessionStorage.clear();
        }
      };
  
      handleLogoutRedirect();
    }, [userLogoutState]);

  const isAccountForLoggedIn = () => {
    return (
      inProgress === InteractionStatus.None &&
      isEmpty(code) &&
      isEmpty(isForgotpassword)
    );
  };

  const isCurrentAccountPresent = () => {
    const currentAccounts = instance.getAllAccounts();
    return (
      inProgress === InteractionStatus.None &&
      currentAccounts &&
      currentAccounts.length > 0
    );
  };

  const getLoggedIn = (emails: string[]) => {
    if (emails && emails.length > 0) {
      getLoggedInUserByEmail(emails[0]);
    }
  };

  const checkActiveAccountForLoggedIn = () => {
    const currentAccounts = instance.getAllAccounts();
    if (isCurrentAccountPresent()) {
      instance.setActiveAccount(currentAccounts[0]);
      const accessTokenRequest = {
        scopes: [b2cScope],
        account: currentAccounts[0],
      };
      instance
        .acquireTokenSilent(accessTokenRequest)
        .then((accessTokenResponse: any) => {
          if (accessTokenResponse) {
            const accessToken = accessTokenResponse.accessToken;
            const decodedToken = jwtDecode<JwtPayload>(accessToken);
            const emails = decodedToken.emails;
            getLoggedIn(emails);
          }
        })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
  };

  useEffect(() => {
    if (instance && isAccountForLoggedIn()) {
      checkActiveAccountForLoggedIn();
    }
  }, [instance, inProgress, getLoggedInUserByEmail]);

  const callMfaCode = (loggedInUser: MyAccountModel, type: number) => {
    if (!isEmpty(loggedInUser.providerPortalUser.email)) {
      const payload = {
        userId: loggedInUser.providerPortalUser.userId,
        email: loggedInUser.providerPortalUser.email,
      };
      type === Numbers.ONE ? sendMfaCode(payload) : resendMfaCode(payload);
    }
  };

  const handleModalClose = (_event: SyntheticEvent, reason: string) => {
    if (reason && reason === "backdropClick") {
      //do not close the modal
      return;
    }
  };

  const handleSendMfaOnClick = (mfaValue: any) => {
    if (!isEmpty(mfaValue)) {
      verifyMfaCode({
        userId: loggedInUser.providerPortalUser.userId,
        email: loggedInUser.providerPortalUser.email,
        otp: Number(mfaValue.mfaOtpCodeInput),
      });
    }
  };

  const handleCloseMfaOnClick = () => {
    logoutPortalApp();
  };

  const userContextValue = useMemo(
    () => ({
      roleId: getValue(loggedInUser, "providerPortalUser.roleId", ""),
      userId: getValue(loggedInUser, "providerPortalUser.userId", ""),
      email: getValue(loggedInUser, "providerPortalUser.email", ""),
    }),
    [loggedInUser],
  );

  useEffect(() => {
    if (
      getValue(samlResponseState, "accessToken") &&
      getValue(samlResponseState, "accessToken").length > 0
    ) {
      setSessionStorage(
        "samlToken",
        getValue(samlResponseState, "accessToken"),
      );
      setSessionStorage(
        "samlUserEmail",
        getValue(samlResponseState, "user.email"),
      );
      // schedule refresh using expiresIn from SAML response if present
      const expiresInRaw = getValue(samlResponseState, "expiresIn") || getValue(samlResponseState, "expires_in") || getValue(samlResponseState, "expires");
      const expiresIn = Number(expiresInRaw) || 0;
      const expiredAccessToken = getSessionStorage("samlToken");
      if (expiresIn > 0) {
        scheduleRefresh(expiresIn, expiredAccessToken);
      }
      setIsSAMLTokenAvailable(true);
      getConfig();
    }
  }, [samlResponseState]);

  useEffect(() => {
    const newToken = getValue(refreshTokenResponseState, "accessToken")

    if (newToken) {
      const email = getValue(refreshTokenResponseState, "user.email") || getValue(samlResponseState, "user.email")
      const expiresIn = Number(getValue(refreshTokenResponseState, "expiresIn"));

      setSessionStorage("samlToken", newToken);
      const expiredAccessToken = getSessionStorage("samlToken");
      if (email) setSessionStorage("samlUserEmail", email);
      if (expiresIn > 0) scheduleRefresh(expiresIn, expiredAccessToken);
    }
  }, [refreshTokenResponseState]);

  useEffect(() => {
    if (
      getValue(samlResponseState, "accessToken") &&
      getValue(samlResponseState, "accessToken").length > 0 &&
      Number(getValue(config, "REACT_APP_PORTAL_MAX_SERVICE_PROVIDER_COUNT")) > 0
    ) {
      setTimeout(() => {
        getLoggedInUserByEmail(getValue(samlResponseState, "user.email"));
      }, 1000);

      if (masterFeatureAccessState && masterFeatureAccessState.length > 0) {
        const isFeatureEnabled = hasAccess(
          getValue(samlResponseState, "user.roleId"),
          masterFeatureAccessState,
          Numbers.Eight
        );

        const referralDetails = getValue(samlResponseState, "referralDetails");
        const referralId = getValue(referralDetails, "referralId");

        if (!isFeatureEnabled) {
          setSamlReferralRoute(ApplicationRoute.HOME);
        } else if (referralDetails === null || referralDetails === undefined) {
          setSamlReferralRoute(ApplicationRoute.HOME);
        } else if (Number(referralId) === 0) {
          toastType.error(
            `The Case Number ${getValue(samlResponseState, "externalHealthPlanId")} does not match any records in our system. Please search for your member to continue.`
          );
          setSamlReferralRoute(ApplicationRoute.HOME);
        } else if (Number(referralId) > 0) {
          const encryptedReferralId = encryptId(referralId);
          const encryptedProviderId = encryptId(
            getValue(referralDetails, "serviceProviderId")
          );
          setSamlReferralRoute(
            `${ApplicationRoute.RE_AUTH_MEMBER_INFO}/${encryptedReferralId}/${encryptedProviderId}`
          );
        }
      }
    }
  }, [config, masterFeatureAccessState, samlResponseState]);

  useEffect(() => {
    if (
      isSAMLTokenAvailable &&
      samlReferralRoute &&
      getValue(loggedInUser, "providerPortalUser.email", "").length > 0
    ) {
      navigate(samlReferralRoute);
      setSamlReferralRoute(null);
    }
  }, [loggedInUser, isSAMLTokenAvailable, samlReferralRoute]);

  const ProtectedContent = () => {
    const { accounts } = useMsal();
    const account =
      accounts.length > Numbers.ZERO
        ? accounts[Numbers.ZERO]
        : ({} as AccountInfo);

    if (accounts.length === Numbers.ZERO) {
      // no accounts signed-in, attempt to sign a user in
      instance.loginRedirect(loginRequest);
    } else {
      instance.setActiveAccount(account);
    }

    const mfaVerification = (openMfaModal: boolean) => {
      if (openMfaModal) {
        return (
          <MFAContent
            handleSendMfaOnClick={handleSendMfaOnClick}
            callMfaCode={callMfaCode}
            loggedInUser={loggedInUser}
            handleModalClose={handleModalClose}
            handleCloseMfaOnClick={handleCloseMfaOnClick}
            userSendMfaInfoState={userSendMfaInfoState}
            openMfaModal={openMfaModal}
          />
        );
      }
      return <>Loading...</>;
    };

    return (
      <>
        {isSAMLTokenAvailable && (
          <UserContext.Provider value={userContextValue}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Grid container direction="column">
                  <Grid sx={HeaderStickyContainer}>
                    <Header />
                  </Grid>
                </Grid>
                <Grid sx={LeftNavStickyContainer}>
                  <LeftNavbar />
                </Grid>
              </Grid>
            </Grid>
          </UserContext.Provider>
        )}

        {!isSAMLTokenAvailable && !mfaVerified
          ? mfaVerification(openMfaModal)
          : !isSAMLTokenAvailable && (
            <UserContext.Provider value={userContextValue}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Grid container direction="column">
                    <Grid sx={HeaderStickyContainer}>
                      <Header />
                    </Grid>
                    <Grid sx={LeftNavStickyContainer}>
                      <LeftNavbar />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </UserContext.Provider>
          )}
        <CentralLoader />
      </>
    );
  };

  /**
   * Navigation for User Registration/ResetPassword/ForgotPassword
   */
  const userCheck = (logoutPortalApp: () => void) => {
    let check =
      !isEmpty(code) &&
      !isEmpty(getValue(userByClaims, "email", "")) &&
      isTextPresent === Numbers.TWO;

    if (check) {
      return (
        <Routes>
          <Route
            path={`${ApplicationRoute.USER_REGISTRATION}`}
            element={<UserRegistration userByClaimsState={userByClaims} />}
          />
        </Routes>
      );
    }

    check =
      !isEmpty(code) &&
      !isEmpty(getValue(userByClaims, "email", "")) &&
      isTextPresent === Numbers.ONE;

    if (check) {
      return (
        <Routes>
          <Route
            path={`${ApplicationRoute.RESET_PASSWORD}`}
            element={<ResetPassword userByClaimsState={userByClaims} />}
          />
        </Routes>
      );
    }

    check = !isEmpty(code);

    if (check) {
      return (
        <Routes>
          <Route
            path={`${ApplicationRoute.FORGOT_PASSWORD}`}
            element={<ForgotPassword />}
          />
        </Routes>
      );
    }

    return isSAMLTokenAvailable ? (
      <>
      <UserContext.Provider value={userContextValue}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Grid container direction="column">
              <Grid sx={HeaderStickyContainer}>
                <Header />
              </Grid>
              <Grid sx={LeftNavStickyContainer}>
                <LeftNavbar />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </UserContext.Provider>
      <CentralLoader />
      </>
    ) : (
      <>
        <AuthenticatedTemplate>
          <SessionTimeout logoutPortalApp={logoutPortalApp}  />
          <ProtectedContent />
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <UnauthenticatedContent />
        </UnauthenticatedTemplate>
      </>
    );
  };

  return (
    <>
      <SnackbarProvider maxSnack={3} anchorOrigin={SnackbarAnchorOrigin}>
        <SnackbarUtilsConfigurator />
        {userCheck(logoutPortalApp)}
      </SnackbarProvider>
    </>
  );
};

const UnauthenticatedContent = () => {
  return (
    <Grid container spacing={2} sx={UnauthenticatedContainer}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h2" align="left">
          Loading...
        </Typography>
      </Grid>
    </Grid>
  );
};

export default AppContent;
